//@flow
import * as Immutable from 'immutable';

import { LatLng } from 'common/models/flow/LatLng';
import type { LatLngModelType } from 'common/models/flow/LatLng';

//Validation stuff
import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import NumberUtils from 'common/utils/NumberUtils';
import I18nHelper from 'common/helpers/I18nHelper';
import DataUtils from 'common/utils/DataUtils';

const translationsNamespace = ['validations'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

@validatable
class InternalLatLng extends LatLng {
    static fromJS(json: LatLngModelType): InternalLatLng {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    equals(latLng: LatLng) {
        if (latLng.lat === this.lat && latLng.lng === this.lng) {
            return true;
        }
        return false;
    }

    @validate
    setLat(lat: number): this {
        return this.clone(this._state.set('lat', lat));
    }

    validateLat() {
        if (InternalLatLng.isLatLngEmpty(this)) {
            // if both are empty, don't show error
            return;
        }
        if (DataUtils.isEmpty(this.lat) && !DataUtils.isEmpty(this.lng)) {
            return nsTranslate('latLng.latNotPresent');
        }
        if (!NumberUtils.isNumeric(this.lat)) {
            return nsTranslate('latLng.latNotANumber');
        }
        if (!(Number(this.lat) <= 90 && Number(this.lat) >= -90)) {
            return nsTranslate('latLng.latNotInRange');
        }
    }

    @validate
    setLng(lng: number): this {
        return this.clone(this._state.set('lng', lng));
    }

    validateLng() {
        if (InternalLatLng.isLatLngEmpty(this)) {
            // if both are empty, don't show error
            return;
        }
        if (!DataUtils.isEmpty(this.lat) && DataUtils.isEmpty(this.lng)) {
            return nsTranslate('latLng.lngNotPresent');
        }
        if (!NumberUtils.isNumeric(this.lng)) {
            return nsTranslate('latLng.lngNotANumber');
        }
        if (!(Number(this.lng) <= 180 && Number(this.lng) >= -180)) {
            return nsTranslate('latLng.lngNotInRange');
        }
    }

    isValid(): boolean {
        const { lat, lng } = this;
        if (
            NumberUtils.isNumeric(lat) &&
            NumberUtils.isNumeric(lng) &&
            Number(lat) <= 90 &&
            Number(lat) >= -90 &&
            Number(lng) <= 180 &&
            Number(lng) >= -180
        ) {
            return true;
        }
        return false;
    }

    isDifferent(latLng?: LatLng): boolean {
        const { lat, lng } = this;
        const { lat: newLat, lng: newLng } = latLng || {};
        if (lat === newLat && lng === newLng) {
            return false;
        }
        return true;
    }

    flatten(): Array<number> {
        const { lat, lng } = this;
        return [lat, lng];
    }

    getLatLngString() {
        return this.isValid() ? `${this.lat}, ${this.lng}` : null;
    }

    defaultSanitize(): ?this {
        return InternalLatLng.sanitizeLatLng(this);
    }

    isEmpty() {
        return InternalLatLng.isLatLngEmpty(this);
    }

    static isLatLngEmpty(latLng): ?(InternalLatLng | LatLngModelType) {
        const { lat, lng } = latLng;
        if (DataUtils.isEmpty(lat) && DataUtils.isEmpty(lng)) {
            // if both are empty, don't show error
            return true;
        }
        return false;
    }

    static sanitizeLatLng(latLng): ?(InternalLatLng | LatLngModelType) {
        const { lat, lng } = latLng;
        const trimmedLat = lat ? new String(lat).trim() : lat;
        const trimmedLng = lng ? new String(lng).trim() : lng;
        if (!trimmedLat && !trimmedLng) {
            return null;
        }
        return latLng;
    }

    static getDefault(): InternalLatLng {
        return this.fromJS({
            lat: null,
            lng: null,
            accuracy: null
        });
    }
}

export { InternalLatLng as LatLng };
